<script setup lang="ts">
import {
  useZoomImageClick,
  useZoomImageMove,
  useZoomImageWheel,
} from "@zoom-image/vue";

const { imageUrl, enableZoom = false } = defineProps<{
  imageUrl: string;
  enableZoom?: boolean;
}>();

const emit = defineEmits<{
  (e: "click"): void;
  (e: "keydown"): void;
}>();

const TOTAL_IMAGES = 36;
const ZOOM_FACTOR_MOVE = 2.5;
const ZOOM_FACTOR_CLICK = 2;
const PHYSICS_SENSITIVITY = 5;

const imageZoomContainerRef = ref<HTMLDivElement>();
const sliderContainerRef = ref<HTMLDivElement>();
const isLoading = ref(false);
const preloadedImages = ref<Map<number, string>>(new Map());
const loadedImageCount = ref(0);

const isZoomedIn = ref(false);

const { isTouchDevice, isFirefox } = useDeviceInformation();

// Physics-based dragging while keeping slider control
const {
  currentImageIndex,
  isDragging,
  hasDragged,
  isAnimating,
  startDrag,
  onDrag,
  endDrag,
  stopAnimation,
  setImageIndex,
} = use360Physics(TOTAL_IMAGES, 0, PHYSICS_SENSITIVITY, isTouchDevice);

const { createZoomImage: createZoomImageWheel, setZoomImageState } =
  useZoomImageWheel();
const { createZoomImage: createZoomImageMove } = useZoomImageMove();
const { createZoomImage: createZoomImageClick } = useZoomImageClick();

const shouldShowLoading = computed(() => {
  return isLoading.value;
});

const shouldShowSlider = computed(() => {
  return isReady.value && !isDragging.value && !isAnimating.value;
});

const currentImageUrl = computed(() => {
  const index = currentImageIndex.value;
  const preloadedUrl = preloadedImages.value.get(index);

  if (preloadedUrl) {
    return preloadedUrl;
  }

  // Don't show original URLs while loading - wait for all images to be preloaded
  if (isLoading.value || loadedImageCount.value === 0) {
    return "";
  }

  const imageNumber = String(index + 1).padStart(2, "0");

  return `${imageUrl}${imageNumber}.jpg`;
});

const isReady = computed(() => {
  return loadedImageCount.value > 0 && !isLoading.value;
});

const preloadImages = async () => {
  isLoading.value = true;

  // Load all images at once - simpler and more predictable
  const loadImageAsBlob = (index: number): Promise<void> => {
    return new Promise((resolve) => {
      const imageNumber = String(index + 1).padStart(2, "0");
      const url = `${imageUrl}${imageNumber}.jpg`;

      if (!url) {
        resolve();

        return;
      }

      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = () => {
        const canvas = document.createElement("canvas");
        const ctx = canvas.getContext("2d");
        if (ctx) {
          canvas.width = img.naturalWidth;
          canvas.height = img.naturalHeight;
          ctx.drawImage(img, 0, 0);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const blobUrl = URL.createObjectURL(blob);
                preloadedImages.value.set(index, blobUrl);
                loadedImageCount.value++;
              }
              resolve();
            },
            "image/jpeg",
            1,
          );
        } else {
          // Fallback: use original URL
          preloadedImages.value.set(index, url);
          loadedImageCount.value++;
          resolve();
        }
      };

      img.onerror = () => {
        resolve();
      };

      img.src = url;
    });
  };

  // Load all images in parallel
  const allIndexes = Array.from({ length: TOTAL_IMAGES }, (_, i) => i);

  // only preload the first 4 and last 4 images
  const preloadedIndexes = [
    ...allIndexes.slice(0, 4),
    ...allIndexes.slice(-3),
    TOTAL_IMAGES,
  ];

  await Promise.allSettled(preloadedIndexes.map(loadImageAsBlob));

  isLoading.value = false;

  const restLoad = allIndexes.filter((i) => !preloadedIndexes.includes(i));

  await Promise.allSettled(restLoad.map(loadImageAsBlob));
};

const getZoomImageUrl = () => {
  if (!enableZoom) return undefined;

  const imageNumber = String(currentImageIndex.value + 1).padStart(2, "0");

  return `${imageUrl}${imageNumber}.jpg`;
};

const initializeMoveZoom = () => {
  if (!enableZoom || !imageZoomContainerRef.value) return;

  createZoomImageMove(imageZoomContainerRef.value, {
    zoomImageSource: getZoomImageUrl(),
    zoomFactor: ZOOM_FACTOR_MOVE,
  });

  const container = imageZoomContainerRef.value;
  const observer = new MutationObserver(() => {
    const hasZoomedChild = Array.from(container.children).some((child) => {
      const element = child as HTMLElement;
      const transform = element.style.transform;

      return Boolean(
        transform &&
          transform.includes("scale") &&
          !transform.includes("scale(1)") &&
          !transform.includes("scale(0)"),
      );
    });
    isZoomedIn.value = hasZoomedChild;
  });

  observer.observe(container, {
    attributes: true,
    attributeFilter: ["style"],
    subtree: true,
    childList: true,
  });
};

const initializeClickZoom = () => {
  if (!enableZoom || !imageZoomContainerRef.value) return;

  createZoomImageClick(imageZoomContainerRef.value, {
    zoomImageSource: getZoomImageUrl(),
    zoomFactor: ZOOM_FACTOR_CLICK,
  });
};

const initializeWheelZoom = () => {
  if (!enableZoom || !imageZoomContainerRef.value) return;

  createZoomImageWheel(imageZoomContainerRef.value);

  if (isTouchDevice.value) {
    const container = imageZoomContainerRef.value;

    const handleZoomChange = () => {
      if (container) {
        const hasZoomedChild = Array.from(container.children).some((child) => {
          const element = child as HTMLElement;
          const transform = element.style.transform;

          return Boolean(
            transform &&
              transform.includes("scale") &&
              !transform.includes("scale(1)") &&
              !transform.includes("scale(0)"),
          );
        });
        isZoomedIn.value = hasZoomedChild;
      }
    };

    container.addEventListener("wheel", handleZoomChange);
    container.addEventListener("touchend", handleZoomChange);
    container.addEventListener("gestureend", handleZoomChange);
  }
};

const initializeZoom = () => {
  if (
    !enableZoom ||
    !isReady.value ||
    !currentImageUrl.value ||
    !imageZoomContainerRef.value
  ) {
    return;
  }

  resetZoomState();

  if (isTouchDevice.value) {
    initializeWheelZoom();
  } else if (isFirefox.value) {
    initializeClickZoom();
  } else {
    initializeMoveZoom();
  }
};

const handleImageClick = () => {
  if (!enableZoom && !hasDragged.value) {
    emit("click");
  }
};

const handleSliderInput = (event: Event) => {
  event.stopPropagation();
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value, 10);

  setImageIndex(value);
};

const handleSliderTouch = (event: TouchEvent) => {
  event.stopPropagation();
};

const shouldDisableRotation = () => {
  return (
    (enableZoom && !isTouchDevice.value) ||
    (isTouchDevice.value && isZoomedIn.value)
  );
};

const isSliderEvent = (event: MouseEvent) => {
  return (
    sliderContainerRef.value &&
    sliderContainerRef.value.contains(event.target as Node)
  );
};

const handleMouseDown = (event: MouseEvent) => {
  if (shouldDisableRotation()) return;

  event.preventDefault();
  startDrag(event.clientX);
  document.body.style.cursor = "grabbing";
};

const handleMouseMove = (event: MouseEvent) => {
  if (shouldDisableRotation() || isSliderEvent(event) || !isDragging.value) {
    return;
  }

  event.preventDefault();
  onDrag(event.clientX);
};

const handleMouseUp = (event: MouseEvent) => {
  if (isDragging.value) {
    endDrag();
    document!.body!.style!.cursor = "";
  }

  if (shouldDisableRotation() || isSliderEvent(event)) return;
};

const lastTouchMoveTime = ref(0);

const handleImageTouchStart = (event: TouchEvent) => {
  if (shouldDisableRotation()) return;

  const touch = event.touches[0];
  if (touch) {
    startDrag(touch.clientX);
  }
};

const handleTouchMove = (event: TouchEvent) => {
  if (shouldDisableRotation() || !isDragging.value) return;

  if (!isZoomedIn.value) {
    event.preventDefault();
  }

  const touch = event.touches[0];
  const now = Date.now();

  if (touch && now - lastTouchMoveTime.value > 8) {
    lastTouchMoveTime.value = now;
    onDrag(touch.clientX);
  }
};

const handleTouchEnd = () => {
  if (isZoomedIn.value) return;

  endDrag();
};

const handleMouseLeave = () => {
  if (isDragging.value) {
    endDrag();
  }
  document!.body!.style!.cursor = "";
};

const handleContainerKeydown = () => {
  emit("keydown");
};

const resetZoomState = () => {
  if (imageZoomContainerRef.value) {
    setZoomImageState({
      currentRotation: 0,
      currentZoom: 0,
      enable: true,
    });

    isZoomedIn.value = false;
  }
};

const disableZoom = () => {
  if (imageZoomContainerRef.value) {
    setZoomImageState({
      currentRotation: 0,
      currentZoom: 0,
      enable: false,
    });
  }
};

onMounted(() => {
  nextTick(() => {
    resetZoomState();
  });

  if (!isLoading.value && loadedImageCount.value === 0) {
    preloadImages();
  }
});

watch(
  [() => enableZoom, currentImageUrl, () => isReady.value],
  () => {
    nextTick(() => {
      initializeZoom();
    });
  },
  { flush: "post" },
);

watch(
  () => enableZoom,
  (newEnableZoom) => {
    if (!newEnableZoom) {
      disableZoom();
    }
  },
);

onUnmounted(() => {
  // Clean up blob URLs to prevent memory leaks
  preloadedImages.value.forEach((blobUrl) => {
    URL.revokeObjectURL(blobUrl);
  });

  // Reset cursor state
  document!.body!.style!.cursor = "";

  stopAnimation();
});
</script>

<template>
  <div
    class="base-360-viewer"
    :class="{
      'is-loading': shouldShowLoading,
      'is-dragging': isDragging,
    }"
    tabindex="0"
    role="img"
    aria-label="360° Product View. Use arrow keys or slider to rotate."
    @keydown="handleContainerKeydown"
  >
    <div
      class="base-360-viewer__image-container"
      :class="{
        'base-360-viewer__image-container--zoomed': isTouchDevice && isZoomedIn,
      }"
      data-test="360-viewer-image"
    >
      <div
        v-if="shouldShowLoading"
        class="base-360-viewer__loading-container"
      >
        <BaseLoadingSpinner />
      </div>

      <div
        v-else
        class="base-360-viewer__zoom-container"
        :class="{
          'cursor-grab': !isDragging,
          'cursor-grabbing': isDragging,
          'cursor-move': enableZoom,
        }"
        data-test="360-viewer-zoom-container"
        @click="handleImageClick"
        @keydown="handleImageClick"
        @mousemove="handleMouseMove"
        @mousedown="handleMouseDown"
        @mouseup="handleMouseUp"
        @mouseleave="handleMouseLeave"
        @blur="handleMouseLeave"
      >
        <div
          v-if="enableZoom"
          ref="imageZoomContainerRef"
          :class="
            isTouchDevice
              ? 'base-360-viewer__mobile-zoom-container'
              : 'base-360-viewer__image-zoom-wrapper'
          "
        >
          <NuxtImg
            :src="currentImageUrl"
            alt="360° Product View - 360° view"
            class="base-360-viewer__image"
            draggable="false"
            @touchmove="handleTouchMove"
            @touchend.passive="handleTouchEnd"
            @touchstart.passive="handleImageTouchStart"
          />
        </div>
        <NuxtImg
          v-else
          :src="currentImageUrl"
          alt="360° Product View - 360° view"
          class="base-360-viewer__image"
          draggable="false"
          @touchmove="handleTouchMove"
          @touchend.passive="handleTouchEnd"
        />
      </div>
      <Transition
        name="slider-fade"
        enter-active-class="transition-all duration-500 ease-in-out"
        enter-from-class="opacity-0 translate-y-2"
        enter-to-class="opacity-100 translate-y-0"
        leave-active-class="transition-all duration-500 ease-in-out"
        leave-from-class="opacity-100 translate-y-0"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          v-if="shouldShowSlider"
          ref="sliderContainerRef"
          class="base-360-viewer__slider-container"
          data-test="360-viewer-slider"
          @click.stop
          @mousedown.stop
        >
          <input
            type="range"
            :min="0"
            :max="TOTAL_IMAGES - 1"
            :value="currentImageIndex"
            class="base-360-viewer__slider"
            aria-label="Rotate 360° view"
            @input="handleSliderInput"
            @mousedown.stop
            @touchstart="handleSliderTouch"
            @touchmove="handleSliderTouch"
            @touchend="handleSliderTouch"
          />
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.base-360-viewer {
  @apply relative w-full overflow-hidden;

  &.is-loading {
    @apply cursor-wait;
  }

  &.is-dragging {
    @apply select-none;
  }

  &__image-container {
    @apply relative w-full h-full flex items-center justify-center pb-0;

    &--zoomed {
      @apply pb-0;
    }
  }

  &__loading-container {
    & .loading-spinner {
      @apply static;
    }
  }

  &__zoom-container {
    @apply w-full h-full flex items-center justify-center overflow-hidden relative touch-pan-x touch-pan-y touch-pinch-zoom;

    &:active {
      @apply cursor-grabbing;
    }
  }

  &__mobile-zoom-container {
    @apply flex items-center justify-center relative overflow-visible !important;

    .base-360-viewer__image {
      @apply w-full h-full object-contain select-none pointer-events-auto;
    }
  }

  &__image-zoom-wrapper {
    @apply h-full flex items-center justify-center relative;
  }

  &__image {
    @apply max-w-full max-h-full object-contain select-none pointer-events-auto;
    transform-origin: center center;
    transform: scale(1);
  }

  &__slider-container {
    @apply absolute bottom-0 left-1/2 -translate-x-1/2 w-3/5 max-w-xs z-10 transition-all duration-300 ease-in-out;
    @apply touch-pan-y touch-pinch-zoom overscroll-x-contain;
  }

  &__slider {
    @apply w-full h-12 bg-transparent appearance-none cursor-pointer;
    @apply touch-pan-x;

    &::-webkit-slider-runnable-track {
      @apply h-0.5 bg-white rounded-full;
    }

    &::-moz-range-track {
      @apply h-0.5 bg-white rounded-full;
    }

    &::-webkit-slider-thumb {
      @apply appearance-none w-4 h-4 -mt-[7px] bg-white rounded-full cursor-pointer;
      @apply min-w-4 min-h-4;
    }

    &::-moz-range-thumb {
      @apply w-4 h-4 -mt-[7px] bg-white rounded-full cursor-pointer border-none;
      @apply min-w-4 min-h-4;
    }
  }

  @media (max-width: 768px) {
    &__slider-container {
      @apply bottom-4 w-3/5;
      @apply touch-pan-y touch-pinch-zoom overscroll-x-contain;
    }

    &__slider {
      @apply h-12 bg-transparent appearance-none cursor-pointer;
      @apply touch-pan-x;

      &::-webkit-slider-runnable-track {
        @apply h-0.5 bg-blue-500 rounded-full;
      }

      &::-moz-range-track {
        @apply h-0.5 bg-blue-500 rounded-full;
      }

      &::-webkit-slider-thumb {
        @apply min-w-4 min-h-4 bg-blue-600;
      }

      &::-moz-range-thumb {
        @apply min-w-4 min-h-4 bg-blue-600;
      }
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .base-360-viewer {
    &__image {
      @apply transition-none;
    }

    &__slider::-webkit-slider-thumb {
      @apply transition-none;
    }

    &__slider::-moz-range-thumb {
      @apply transition-none;
    }
  }
}

@media (hover: none) and (pointer: coarse) {
  .base-360-viewer {
    &__zoom-container {
      @apply cursor-auto;

      &:active {
        @apply cursor-auto;
      }
    }
  }
}
</style>
